import styled from "@emotion/styled";
import * as Sentry from "@sentry/react";
import { Link, useNavigate } from "@tanstack/react-router";
import { Popconfirm } from "antd5";
import { Flex, Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import toast from "components/ui/toast/Toast";
import Tooltip from "components/ui/tooltip/Tooltip";
import { appRoutes } from "lib/appRoutes";
import { useVariableValue } from "lib/featureFlags";
import { AuthMode } from "lib/generated/integration-api";
import { useParagonCrmProvider } from "lib/hooks/api/integrations/useFindIntegrationProvider";
import useParagonUser from "lib/hooks/api/integrations/useParagonUser";
import { useRecordParagonInstall } from "lib/hooks/api/integrations/useRecordParagonInstall";
import { useRemoveParagonInstall } from "lib/hooks/api/integrations/useRemoveParagonInstall";
import { useUninstallProvider } from "lib/hooks/api/integrations/useUninstallProvider";
import { paragon } from "lib/integrations/paragon";
import { EventNames, useTracking } from "lib/tracking";
import { ParagonCrmProvider, readOrganisationUrl } from "./crmProviders";

type Props = {
  /** The CRM this card connects. See `crmProviders.ts`. */
  provider: ParagonCrmProvider;
  /**
   * Name of the CRM already connected through the integrations service, if any. Only one CRM can
   * be connected at a time, so its presence disables Connect.
   */
  installedProviderName: string | undefined;
  /**
   * The company's live connection to this same CRM through our own OAuth app, if it has one. Set
   * only while a migration is in progress, and what turns this card from a second tile offering
   * the same CRM into the one card that CRM has.
   */
  nativeInstall?: { providerId: string };
  /**
   * Whether the company's existing connections are still being read. Until they are, a native
   * install reads exactly like no connection at all, and offering Connect would open Paragon for a
   * company that should have been asked to confirm replacing what it already has.
   */
  isLoadingConnections?: boolean;
  /**
   * Set when reading them failed, which is not the same as finding none: the company may well have
   * a CRM we simply could not see. Connecting from there would replace it without ever saying so.
   */
  connectionsUnknown?: boolean;
};

/**
 * The one card a Paragon-backed CRM has, whichever way the company is currently connected to it.
 *
 * Connecting happens in the Paragon Connect Portal; the field mapping that decides what each push
 * writes is configured on our own page, which Configure opens. Renders nothing unless the
 * provider's flag is on and the integration is enabled in the Paragon project.
 *
 * A company still on our own OAuth app for this CRM is offered Reconnect rather than a disabled
 * Connect. Which app holds the credential is ours to know and not theirs, and the service already
 * treats connecting through Paragon as the cutover: it retires the old install exactly as
 * Disconnect would, and moves the records already pushed onto the new one.
 *
 * Distinct from CMSConnectionCard, which connects cloud storage through Paragon but then drives
 * managed syncs through app-service — a different backend and a file picker instead of a mapping.
 */
export default function CRMConnectionCard({
  provider,
  installedProviderName,
  nativeInstall,
  isLoadingConnections,
  connectionsUnknown,
}: Props): JSX.Element | null {
  const isEnabled = useVariableValue(provider.flag, false);
  const { user } = useParagonUser();
  const {
    data: install,
    isInitialLoading: isLoadingInstall,
    isError: installUnknown,
  } = useParagonCrmProvider();
  const { logEvent } = useTracking();
  const navigate = useNavigate();
  const { mutateAsync: recordInstall } = useRecordParagonInstall();
  const { mutateAsync: removeInstall } = useRemoveParagonInstall();
  const { mutate: uninstallNative } = useUninstallProvider();

  // Paragon owns the name and icon, so a provider it doesn't offer in this environment has no card.
  const metadata = paragon
    .getIntegrationMetadata()
    .find((integration) => integration.type === provider.integrationKey);

  // Enabling the integration in Paragon is per-environment, so the flag is what makes it
  // per-company — without it, every company in the environment would see the card.
  if (!isEnabled || !metadata) return null;

  /**
   * Connected means both sides agree: Paragon holds a live credential, and the service has recorded
   * an install against it. Either alone is a half-finished connection that nothing can push through.
   *
   * A reconnect whose recording failed is exactly that — Paragon issues the credential before the
   * service hears anything — and taking Paragon's word for it would offer a Configure page that
   * reports no CRM, a Disconnect for an install that was never recorded, and a page that counts the
   * company as having a CRM, so no other card would offer to connect one either.
   */
  /**
   * Whether this card is in a position to offer a connection at all. A read that failed is not an
   * answer: the company may hold a CRM we could not see, and connecting over it is the one action
   * here that cannot be taken back.
   */
  const cannotCheckConnections = !!connectionsUnknown || installUnknown;

  const isRecorded =
    install?.authMode === AuthMode.Paragon && install.providerType === provider.providerType;
  const isConnected = isRecorded && !!user?.integrations?.[provider.integrationKey]?.enabled;

  const logConfigureClicked = () =>
    logEvent(EventNames.configureIntegrationClicked, {
      "Context source": "Integration card",
      Provider: provider.providerType,
    });

  /**
   * Tells the integrations service the company has this CRM connected. Nothing about the field
   * mapping is needed here — the object to push to travels with each push, read from the mapping
   * at that point.
   */
  const recordConnection = async (): Promise<void> => {
    const connection = paragon.getUser();
    const credentialId = connection.authenticated
      ? connection.integrations?.[provider.integrationKey]?.credentialId
      : undefined;
    // Read synchronously from the SDK, so this is the state where the install event has fired
    // before the credential landed. Throwing routes it into the caller's error toast; returning
    // quietly would leave the card looking connected with nothing recorded on our side.
    if (!credentialId) {
      throw new Error(`Paragon reported no credential for the ${metadata.name} connection`);
    }

    // Some CRMs never name their own organisation in anything they return, so the only chance to
    // learn it is here, from what Paragon kept off the token exchange. Missing, the connection is
    // still recorded and pushes still work — only the links back into the CRM stay empty — so a
    // read that fails is reported rather than allowed to fail the connect.
    let organisationUrl: string | undefined;
    if (connection.authenticated) {
      try {
        organisationUrl = await readOrganisationUrl(provider, connection, (path) =>
          // body and headers are not optional on the SDK's options, so a read names them empty.
          paragon.request(provider.integrationKey, path, {
            method: "GET",
            body: undefined,
            headers: undefined,
          }),
        );
      } catch (error) {
        // A read that throws leaves the links empty exactly as one that finds nothing does. Letting
        // it propagate would abandon a connection Paragon has already made, leaving the card
        // connected with nothing recorded on our side.
        Sentry.captureException(error);
      }
    }
    if (provider.organisationUrlFrom && !organisationUrl) {
      Sentry.captureMessage(
        `Paragon reported no organisation URL for the ${metadata.name} connection; its links will be empty until one is known`,
      );
    }

    await recordInstall({
      providerType: provider.providerType,
      externalProviderId: credentialId,
      ...(organisationUrl ? { organisationUrl } : {}),
    });
  };

  const openPortal = () => {
    try {
      paragon.connect(provider.integrationKey, {
        onSuccess: async () => {
          // Paragon has the connection either way; failing to record it here leaves the service
          // unaware of it, so say so rather than letting the card look connected and do nothing.
          let recorded = true;
          try {
            await recordConnection();
          } catch (error) {
            recorded = false;
            Sentry.captureException(error);
            toast.error(
              `Connected to ${metadata.name}, but saving the connection failed. Please try connecting again.`,
            );
          }
          paragon.closePortal();

          // A CRM with nothing mapped to it accepts no pushes, so the mapping is the next step
          // rather than something to find out about on the first one that is refused. Skipped
          // when the install was not recorded: the page reads through it, so it could only error.
          if (recorded) {
            void navigate(appRoutes.integrationFieldMapping);
          }
        },
      });
    } catch (error) {
      Sentry.captureException(error);
      toast.error(`Failed to open the ${metadata.name} connection window. Please try again.`);
    }
  };

  const disconnect = async () => {
    try {
      await paragon.uninstallIntegration(provider.integrationKey);
      // Paragon does not tell the integrations service about an uninstall, so without this the
      // company keeps an active CRM install whose credentials no longer exist.
      await removeInstall({ providerType: provider.providerType });
      toast.success(`${metadata.name} disconnected.`);
    } catch (error) {
      Sentry.captureException(error);
      toast.error(`Failed to disconnect ${metadata.name}. Please try again.`);
    }
  };

  return (
    <ProviderCardContainer>
      <Flex column gap={8}>
        <IntegrationIcon src={metadata.icon} alt={`${metadata.name} icon`} />
        <Text h2>{metadata.name}</Text>
        <Text description>
          {nativeInstall
            ? `Reconnect ${metadata.name} to move it to the new integration. The records you have already sent keep their links.`
            : `Connect to your ${metadata.name} account to send leads straight to your CRM.`}
        </Text>
      </Flex>
      <ButtonContainer>
        {isLoadingConnections || isLoadingInstall ? (
          <Button loading />
        ) : nativeInstall ? (
          <>
            <Popconfirm
              title={`Reconnect ${metadata.name} through Stotles' new integration? Your current connection will be replaced.`}
              okText="Reconnect"
              cancelText="Cancel"
              onConfirm={() => {
                logEvent(EventNames.connectToIntegrationClicked, {
                  "Context source": "Integration card",
                  Provider: provider.providerType,
                });
                openPortal();
              }}
            >
              <Button>Reconnect</Button>
            </Popconfirm>
            <Popconfirm
              title={`Are you sure you want to disconnect from ${metadata.name}?`}
              okText="Disconnect"
              cancelText="Cancel"
              onConfirm={() => {
                logEvent(EventNames.disconnectFromIntegrationClicked, {
                  "Context source": "Integration card",
                  Provider: provider.providerType,
                });
                // The install this ends is the native one, which the Paragon routes know nothing
                // about — they key on the Paragon-backed provider row.
                uninstallNative(
                  { providerId: nativeInstall.providerId },
                  {
                    onSuccess: () => toast.success(`${metadata.name} disconnected.`),
                    onError: () => {
                      toast.error(`Failed to disconnect ${metadata.name}. Please try again.`);
                    },
                  },
                );
              }}
            >
              <Button buttonStyle="destructive" buttonType="secondary">
                Disconnect
              </Button>
            </Popconfirm>
            {/* The connection this configures is still the native one, so it is the native setup
                page that configures it — the field mapping page only reads a Paragon install. */}
            <Link to="/account-management/integrations/setup">
              <Button
                icon="settings01"
                iconProps={{ color: "sysTextDefault" }}
                buttonType="secondary"
                onClick={logConfigureClicked}
              >
                Configure
              </Button>
            </Link>
          </>
        ) : isConnected ? (
          <>
            <Popconfirm
              title={`Are you sure you want to disconnect from ${metadata.name}?`}
              okText="Disconnect"
              cancelText="Cancel"
              onConfirm={() => {
                logEvent(EventNames.disconnectFromIntegrationClicked, {
                  "Context source": "Integration card",
                  Provider: provider.providerType,
                });
                void disconnect();
              }}
            >
              <Button buttonStyle="destructive">Disconnect</Button>
            </Popconfirm>
            <Button
              as="a"
              icon="settings01"
              iconProps={{ color: "sysTextDefault" }}
              buttonType="secondary"
              {...appRoutes.integrationFieldMapping}
              onClick={logConfigureClicked}
            >
              Configure
            </Button>
          </>
        ) : (
          <Tooltip
            content={connectBlockedReason(
              installedProviderName,
              metadata.name,
              cannotCheckConnections,
            )}
          >
            {/* Span so the tooltip still triggers while the button inside is disabled */}
            <span>
              <Button
                onClick={() => {
                  logEvent(EventNames.connectToIntegrationClicked, {
                    "Context source": "Integration card",
                    Provider: provider.providerType,
                  });
                  openPortal();
                }}
                disabled={!!installedProviderName || cannotCheckConnections}
              >
                Connect
              </Button>
            </span>
          </Tooltip>
        )}
      </ButtonContainer>
    </ProviderCardContainer>
  );
}

// Mirrors the .providerCard rules the HubSpot and Salesforce cards use, so the tiles line up.
const ProviderCardContainer = styled.div(({ theme }) => ({
  backgroundColor: "#ffffff",
  height: 300,
  width: 285,
  border: `2px solid ${theme.colors.sysBorderDecorative}`,
  padding: 24,
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
}));

const ButtonContainer = styled.div({
  display: "flex",
  justifyContent: "space-between",
  // Reconnect sits beside a Disconnect that is wider than the Configure it replaces, so the pair
  // is allowed to wrap rather than squeezing both labels.
  flexWrap: "wrap",
  gap: 8,
});

// Matches the 38px provider marks on the HubSpot and Salesforce cards.
const IntegrationIcon = styled.img({
  width: 38,
  height: 38,
});

/**
 * Why Connect is disabled: either another CRM holds the one connection a company gets, or we could
 * not find out whether one does. This CRM being the one already connected does not reach here —
 * through our own app it is offered Reconnect, and through Paragon it is already showing connected.
 */
function connectBlockedReason(
  installedProviderName: string | undefined,
  name: string,
  cannotCheckConnections: boolean,
): string | undefined {
  if (cannotCheckConnections) {
    return "Stotles could not check which CRM you are connected to. Please reload the page and try again.";
  }
  if (!installedProviderName) return undefined;
  return `You can only connect one CRM at a time. Disconnect ${installedProviderName} to connect to ${name}.`;
}
